/**
 * Badge Category List Table Component
 * Table for displaying and managing badge categories.
 *
 * REFERENCE IMPLEMENTATION for the shared list primitives — copy this shape when rebuilding a
 * page. What it demonstrates:
 *
 *  - columns are DATA (`DataTableColumn<T>[]`), declared once and rendered as a table at sm+ and
 *    as cards below sm. Nothing here knows which mode is showing.
 *  - `primary` marks the column that becomes the card title on a phone; `hideBelow` drops
 *    lower-value columns on narrow screens; `showInCard: false` drops them from the card.
 *  - client-side paging: pass every row plus `page`/`pageSize` and leave `total` UNSET, and
 *    DataTable slices. (Server-paged pages pass `total` and fetch the page themselves.)
 *  - `getRowActions` returns actions as data: icons with tooltips in the table, labelled buttons
 *    (destructive ones in a "More" menu) on a phone card.
 *  - `toolbar` holds the list's search; the list is client-side, so search filters here.
 *  - the delete confirmation is the shared <ConfirmDialog>, not a hand-rolled <Dialog>.
 *
 * This component lives inside the tabbed /badges page, so it does NOT use <PageShell> — the page
 * owns the title. A standalone list page wraps its DataTable in <PageShell> instead.
 */

import { useMemo, useState } from 'react'
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Button,
  FormControlLabel,
  Checkbox,
  Alert,
  Tooltip,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Lock as LockIcon,
} from '@mui/icons-material'
import { DataTable, type DataTableColumn } from '@components/common/DataTable'
import { ConfirmDialog } from '@components/common/ConfirmDialog'
import { SearchField } from '@components/common/SearchField'
import { useBadgeCategories, useDeleteBadgeCategory } from '../../hooks/useBadgeCategories'
import { BadgeCategoryFormDialog } from './BadgeCategoryFormDialog'
import type { BadgeCategoryDto } from '@api/types/badgeCategory'

export function BadgeCategoryListTable() {
  const [showInactive, setShowInactive] = useState(false)
  const [formDialogOpen, setFormDialogOpen] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<BadgeCategoryDto | null>(null)
  const [categoryToDelete, setCategoryToDelete] = useState<BadgeCategoryDto | null>(null)
  const [search, setSearch] = useState('')

  // Paging state lives here; DataTable never owns it.
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)

  const { data: categories, isLoading, error, refetch } = useBadgeCategories(showInactive)
  const deleteMutation = useDeleteBadgeCategory()

  const filtered = useMemo(() => {
    const q = search.toLowerCase()
    if (!q) return categories ?? []
    return (categories ?? []).filter((c) =>
      [c.name, c.displayName].some((v) => v?.toLowerCase().includes(q))
    )
  }, [categories, search])

  const handleCreateClick = () => {
    setSelectedCategory(null)
    setFormDialogOpen(true)
  }

  const handleEditClick = (category: BadgeCategoryDto) => {
    setSelectedCategory(category)
    setFormDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!categoryToDelete) return

    try {
      await deleteMutation.mutateAsync(categoryToDelete.id)
      setCategoryToDelete(null)
    } catch {
      // Error is surfaced by the mutation's own `error` below.
    }
  }

  /**
   * Column definitions. Memoised only because they close over the delete mutation's pending
   * state — a plain const array would be just as correct.
   */
  const columns = useMemo<DataTableColumn<BadgeCategoryDto>[]>(
    () => [
      {
        key: 'icon',
        header: 'Icon/Color',
        width: 110,
        render: (category) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {category.iconName && <Typography variant="h6">{category.iconName}</Typography>}
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: 1,
                backgroundColor: category.color || '#ccc',
                border: '1px solid rgba(0,0,0,0.1)',
              }}
            />
          </Box>
        ),
      },
      {
        key: 'name',
        header: 'Name',
        // The card title on a phone.
        primary: true,
        render: (category) => (
          // Inactive categories stay dimmed, the way the old table dimmed the whole row.
          <Typography variant="body2" fontWeight="medium" sx={{ opacity: category.isActive ? 1 : 0.6 }}>
            {category.name}
          </Typography>
        ),
      },
      {
        key: 'displayName',
        header: 'Display Name',
        render: (category) => <Typography variant="body2">{category.displayName}</Typography>,
      },
      {
        key: 'type',
        header: 'Type',
        // Platform vs Standard is nice to know, not essential — first thing to go when narrow.
        hideBelow: 'md',
        render: (category) =>
          category.isPlatform ? (
            <Chip icon={<LockIcon />} label="Platform" color="warning" size="small" />
          ) : (
            <Chip label="Standard" size="small" variant="outlined" />
          ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (category) => (
          <Chip
            label={category.isActive ? 'Active' : 'Inactive'}
            color={category.isActive ? 'success' : 'default'}
            size="small"
          />
        ),
      },
      {
        key: 'sortOrder',
        header: 'Sort Order',
        align: 'right',
        width: 110,
        hideBelow: 'md',
        render: (category) => <Typography variant="body2">{category.sortOrder}</Typography>,
      },
    ],
    []
  )

  return (
    <Box>
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={showInactive}
                onChange={(e) => {
                  setShowInactive(e.target.checked)
                  setPage(0) // the result set changes size, so the current page may not exist
                }}
              />
            }
            label="Show Inactive"
          />
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={() => refetch()} disabled={isLoading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateClick}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Add Badge Category
        </Button>
      </Box>

      {deleteMutation.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {deleteMutation.error instanceof Error
            ? deleteMutation.error.message
            : 'Failed to delete badge category'}
        </Alert>
      )}

      <DataTable
        rows={filtered}
        columns={columns}
        getRowKey={(category) => String(category.id)}
        loading={isLoading}
        error={
          error ? (error instanceof Error ? error.message : 'Failed to load badge categories') : null
        }
        emptyMessage={search ? 'No categories match that search' : 'No badge categories found'}
        // No `total` => DataTable pages the rows it was given.
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        pageSizeOptions={[10, 25, 50]}
        onRetry={() => refetch()}
        toolbar={
          <SearchField
            value={search}
            onSearch={(q) => {
              setSearch(q)
              setPage(0)
            }}
            placeholder="Search categories"
          />
        }
        getRowActions={(category) => [
          {
            key: 'edit',
            label: 'Edit',
            icon: <EditIcon fontSize="small" />,
            onClick: () => handleEditClick(category),
          },
          {
            key: 'delete',
            label: 'Delete',
            icon: <DeleteIcon fontSize="small" />,
            color: 'error',
            disabled: deleteMutation.isPending || category.isPlatform,
            hint: category.isPlatform ? 'Platform categories cannot be deleted' : undefined,
            onClick: () => setCategoryToDelete(category),
          },
        ]}
      />

      {/* Form Dialog */}
      <BadgeCategoryFormDialog
        open={formDialogOpen}
        onClose={() => {
          setFormDialogOpen(false)
          setSelectedCategory(null)
        }}
        category={selectedCategory}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={Boolean(categoryToDelete)}
        title="Delete Badge Category"
        message={`Are you sure you want to delete the badge category "${categoryToDelete?.displayName ?? ''}"? Badges currently assigned to it will be left without a category. This action cannot be undone.`}
        confirmLabel={deleteMutation.isPending ? 'Deleting...' : 'Delete'}
        destructive
        loading={deleteMutation.isPending}
        onConfirm={handleDeleteConfirm}
        onClose={() => setCategoryToDelete(null)}
      />
    </Box>
  )
}
